/**
 * The directory's "Closed" list (119-07): companies that closed, were acquired
 * or went bankrupt, each as its public record (status + score history).
 *
 * The ordinary directory query cannot show them — the client policy on
 * `company_directory` admits only active / claimed rows — so the list comes
 * from `closed_company_directory` (card fields only) and the score history
 * from `company_public_card`, one query for the whole page.
 */

import React from 'react';
import { View, Text, ActivityIndicator, Pressable } from 'react-native';
import { Archive } from 'lucide-react-native';
import { ClosedCompanyCard } from '@/components/company/ClosedCompanyCard';
import { useThemeColors } from '@/lib/useThemeColors';
import {
  useClosedCompanyDirectory,
  useCompanyPublicCards,
  type ClosedDirectoryEntry,
} from '@/lib/company/public-card';

export function ClosedCompaniesList({
  search,
  onOpen,
}: {
  search: string;
  onOpen: (entry: ClosedDirectoryEntry) => void;
}) {
  const colors = useThemeColors();
  const list = useClosedCompanyDirectory(search);
  const entries = list.data ?? [];
  const cards = useCompanyPublicCards(entries.map((e) => e.startupId));

  if (list.isLoading) {
    return (
      <View testID="closed-list-loading" className="items-center py-12">
        <ActivityIndicator size="large" color={colors.text.tertiary} />
        <Text numberOfLines={1} className="text-sm mt-3" style={{ color: colors.text.secondary }}>
          Loading closed companies...
        </Text>
      </View>
    );
  }

  if (list.isError) {
    return (
      <View testID="closed-list-error" className="items-center py-12" style={{ gap: 12 }}>
        <Text numberOfLines={2} className="text-sm text-center" style={{ color: colors.text.secondary }}>
          Couldn&apos;t load closed companies.
        </Text>
        <View style={{ height: 44, borderRadius: 12, overflow: 'hidden', backgroundColor: colors.surface.tertiary }}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Retry"
            onPress={() => void list.refetch()}
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 18 }}
          >
            <Text numberOfLines={1} style={{ color: colors.text.primary, fontSize: 14, fontWeight: '600' }}>
              Retry
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  if (entries.length === 0) {
    return (
      <View testID="closed-list-empty" className="items-center py-12">
        <Archive size={40} color={colors.text.tertiary} />
        <Text numberOfLines={1} className="text-base font-medium mt-3" style={{ color: colors.text.primary }}>
          No closed companies
        </Text>
        <Text numberOfLines={2} className="text-sm mt-1 text-center" style={{ color: colors.text.secondary }}>
          {search.trim() ? 'None match this search.' : 'Companies that close, are acquired or go bankrupt appear here.'}
        </Text>
      </View>
    );
  }

  return (
    <View testID="closed-list" className="gap-3">
      {entries.map((e) => {
        const card = cards.data?.get(e.startupId.toLowerCase());
        return (
          <ClosedCompanyCard
            key={`closed-${e.directoryId}`}
            testID={`closed-entry-${e.directoryId}`}
            // The listing's own fields stand in only once the history query
            // has answered (a card for every sealed id) — never "no score on
            // record" while it is still loading.
            card={
              card ?? (cards.isLoading || cards.isError
                ? undefined
                : {
                    startupId: e.startupId,
                    name: e.name,
                    logoUrl: e.logoUrl,
                    scoringDomain: null,
                    scoringStage: null,
                    status: e.status,
                    declaredQuarter: null,
                    latest: null,
                    history: [],
                  })
            }
            loading={!card && cards.isLoading}
            error={!card && cards.isError ? true : undefined}
            onRetry={() => void cards.refetch()}
            fallbackName={e.name}
            onPress={() => onOpen(e)}
          />
        );
      })}
    </View>
  );
}
